// Libraries
import { action, computed, observable } from "mobx";

// Custom
import { CustomerWithPortalAccessModel, CustomerWithPortalAccessModelDTO } from "Custom/Models/DashboardModels/SuperAdminModel";
import { InitialState } from "Custom/Models";

export class SuperAdminStore {
    private static readonly SESSION_STORAGE_KEY = "superAdminSelectedCustomer";
    private static readonly SESSION_STORAGE_HIDE_KEY = "superAdminNotificationHidden";

    @observable
    public selectedCustomer: CustomerWithPortalAccessModel | null = null;

    @observable
    public isNotificationVisible: boolean = false;

    @computed
    public get isViewingCustomer(): boolean {
        return this.selectedCustomer !== null;
    }

    @action
    public setSelectedCustomer = (customer: CustomerWithPortalAccessModel | null): void => {
        this.selectedCustomer = customer;
        if (customer != null) {
            this.isNotificationVisible = true;
            this.persistSelectedCustomer(customer);
            this.clearHideFlag();
        } else {
            this.isNotificationVisible = false;
            this.removePersistedCustomer();
        }
    };

    @action
    public clearSelectedCustomer = (): void => {
        this.setSelectedCustomer(null);
    };

    @action
    public hideNotificationForSession = (): void => {
        this.isNotificationVisible = false;
        this.setHideFlag();
    };

    @action
    public init = (): void => {
        // Only attempt to hydrate when running in the browser
        if (typeof window === "undefined") {
            return;
        }

        const persistedCustomer = this.getPersistedCustomer();

        if (persistedCustomer) {
            const customerModel = new CustomerWithPortalAccessModel();
            customerModel.fromDto(persistedCustomer);
            this.selectedCustomer = customerModel;
            this.isNotificationVisible = !this.isNotificationHidden();
        } else {
            this.clearSelectedCustomer();
        }
    };

    private persistSelectedCustomer(customer: CustomerWithPortalAccessModel): void {
        if (typeof window === "undefined") {
            return;
        }

        try {
            const dto = customer.toDto();
            window.sessionStorage.setItem(SuperAdminStore.SESSION_STORAGE_KEY, JSON.stringify(dto));
        } catch {
            // If persistence fails, swallow the error – the bar simply won't persist across reloads.
        }
    }

    private removePersistedCustomer(): void {
        if (typeof window === "undefined") {
            return;
        }

        window.sessionStorage.removeItem(SuperAdminStore.SESSION_STORAGE_KEY);
        this.clearHideFlag();
    }

    private getPersistedCustomer(): CustomerWithPortalAccessModelDTO | null {
        if (typeof window === "undefined") {
            return null;
        }

        const raw = window.sessionStorage.getItem(SuperAdminStore.SESSION_STORAGE_KEY);

        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as CustomerWithPortalAccessModelDTO;
        } catch {
            window.sessionStorage.removeItem(SuperAdminStore.SESSION_STORAGE_KEY);
            return null;
        }
    }

    private isNotificationHidden(): boolean {
        if (typeof window === "undefined") {
            return false;
        }

        return window.sessionStorage.getItem(SuperAdminStore.SESSION_STORAGE_HIDE_KEY) === "true";
    }

    private clearHideFlag(): void {
        if (typeof window === "undefined") {
            return;
        }

        window.sessionStorage.removeItem(SuperAdminStore.SESSION_STORAGE_HIDE_KEY);
    }

    private setHideFlag(): void {
        if (typeof window === "undefined") {
            return;
        }

        window.sessionStorage.setItem(SuperAdminStore.SESSION_STORAGE_HIDE_KEY, "true");
    }
}
